import { Component, ChangeDetectionStrategy, computed, input, signal, TemplateRef, output, Input, effect, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { SortableHeader } from '../../../types/table';
import { TranslateModule } from '@ngx-translate/core';
import { CheckboxModule } from 'primeng/checkbox';

@Component({
  selector: 'app-material-table',
  standalone: true,
  imports: [NgTemplateOutlet, TranslateModule, CheckboxModule],
  templateUrl: './material-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaterialTableComponent<T extends { id: any }> {
  headers = input.required<SortableHeader[]>();
  data = input.required<T[]>();
  rowTemplate = input.required<TemplateRef<{ $implicit: T }>>();
  key = input<keyof T>('id');
  @Input() width = 'min-w-[800px]';

  enablePagination = input(false);
  pageSize = input(10);
  totalCnt = input(0);
  ontoggle = input(false);
  @Input() isPaging = false;

  totalPage = signal(0);

  nextPage = output<number>();
  isNextPage = input(false);

  currentPage = signal(1);
  sortColumn = signal<string | null>(null);
  sortDirection = signal<'asc' | 'desc'>('asc');

  allSelected = input<boolean>(false);
  toggleSelectAll = output<boolean>();

  selectedAllUI = false;

  constructor(private cdr: ChangeDetectorRef) {

    effect(() => {
      this.selectedAllUI = this.allSelected();
    });
    let previousData: T[] | null = null;
    effect(() => {
      const currentData = this.data();
      if (!this.ontoggle() && previousData && previousData !== currentData && !this.isNextPage()) {
        this.currentPage.set(1);
      }

      previousData = currentData;
    });
  }
  // sortedData = computed(() => {
  //   const data = this.data() || [];
  //   const column = this.sortColumn();
  //   if (!column) {  
  //     return data;
  //   }
  //   const direction = this.sortDirection();

  //   return [...data].sort((a, b) => {
  //     const aValue = (a as any)[column];
  //     const bValue = (b as any)[column];

  //     if (aValue < bValue) {
  //       return direction === 'asc' ? -1 : 1;
  //     }
  //     if (aValue > bValue) {
  //       return direction === 'asc' ? 1 : -1;
  //     }
  //     return 0;
  //   });
  // });

  totalPages = computed(() => {
    if (!this.enablePagination()) return 1;
    // const dataLength = this.sortedData()?.length || 0;
    return Math.ceil(this.totalCnt() / this.pageSize());
  });

  paginatedData = computed(() => {
    // const data = this.sortedData();
    if (!this.isPaging) {
      if (!this.enablePagination()) return this.data();
      const startIndex = (this.currentPage() - 1) * this.pageSize();
      return this.data().slice(startIndex, startIndex + this.pageSize());
    }
    else {
      return this.data();
    }
  });

  startIndex = computed(() => (this.currentPage() - 1) * this.pageSize());
  endIndex = computed(() => this.startIndex() + this.pageSize());
  displayEndIndex = computed(() => {
    if (this.isPaging) {
      return this.startIndex() + this.data().length;
    }
    return Math.min(this.endIndex(), this.data().length);
  })

  pageNumbers = computed(() => {
    const total = Math.ceil(this.totalCnt() / this.pageSize())
    const current = this.currentPage();
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: (number | '...')[] = [];

    // Always show first page
    pages.push(1);

    // Ellipsis after first page
    if (current > 3) {
      pages.push('...');
    }

    // Pages around current page
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
      if (!pages.includes(i)) pages.push(i);
    }

    // Ellipsis before last page
    if (current < total - 2) {
      if (!pages.includes('...')) pages.push('...');
    }

    // Always show last page
    if (total > 1) {
      if (!pages.includes(total)) pages.push(total);
    }

    return pages;
  });

  changePage(page: number | 'prev' | 'next') {
    if (page === 'prev') {
      this.nextPage.emit(this.currentPage() - 1)
      this.currentPage.update(p => Math.max(1, p - 1));
    } else if (page === 'next') {
      this.nextPage.emit(this.currentPage() + 1)
      this.currentPage.update(p => Math.min(this.totalPages(), p + 1));
    } else {
      this.currentPage.set(page);
      this.nextPage.emit(page)
    }
  }

  sortBy(key: string) {
    if (this.sortColumn() === key) {
      this.sortDirection.update(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortColumn.set(key);
      this.sortDirection.set('asc');
    }
  }

  onToggleSelectAll($event: any) {
    this.toggleSelectAll.emit($event);
  }
}
